import { Leaf, Heart, Sprout, Globe } from 'lucide-react';
import SEO from './SEO';


const AboutUs = () => {
  const features = [
    {
      icon: Leaf,
      title: 'Natural',
      description: 'All our products are made from pure and natural ingredients from nature.'
    },
    {
      icon: Heart,
      title: 'Health First',
      description: 'Your health and wellness are our top priorities. We ensure the highest quality standards for your health.'
    },
    {
      icon: Sprout,
      title: 'Eco-Friendly',
      description: 'We are committed to environmental sustainability in all our operations to make a better world.'
    }
  ];

  const values = [
    {
      title: 'Our Mission',
      description: 'To provide high-quality, natural herbal products that promote health and wellness while supporting sustainable practices and ethical sourcing.'
    },
    {
      title: 'Our Vision',
      description: 'To become the most trusted name in herbal products, known for quality, authenticity, and commitment to customer health and environmental sustainability.'
    },
    {
      title: 'Our Values',
      description: 'We believe in transparency, quality, sustainability, and putting our customers\' health and well-being first in everything we do.'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
      <SEO 
        title="About Us - Our Mission for Natural Wellness"
        description="Learn about Vihaan Herbals' mission to bring ancient herbal wisdom to modern wellness. We are committed to 100% natural, eco-friendly, and sustainable health solutions."
      />

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-green-600 to-emerald-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 sm:py-28" style={{ paddingTop: '1rem', paddingBottom: '1rem' }}>
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 sm:w-24 sm:h-24 bg-white/20 rounded-full mb-6 backdrop-blur-sm">
              <img
                src="/logo.png"
                alt="Vihaan Herbal Logo"
                className="w-16 h-16 sm:w-20 sm:h-20 object-contain"
                style={{ filter: 'brightness(0) invert(1)' }}
              />
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4 sm:mb-6">
              About Vihaan Herbals
            </h1>
            <p className="text-lg sm:text-xl text-green-50 max-w-3xl mx-auto px-4">
              Your trusted partner in natural health and wellness. We bring you the finest herbal products, 
              carefully sourced and crafted with care for your well-being.
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-6">
        {/* Our Story Section */}
        <div className="mb-6 sm:mb-6">
          <div className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300 p-6 sm:p-8 lg:p-12 border border-gray-100">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6 sm:mb-8 text-center">
              Our Story
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 space-y-4 text-base sm:text-lg leading-relaxed">
              <p>
                Vihaan Herbals was founded with a simple yet powerful mission: to make natural, 
                high-quality herbal products accessible to everyone. We believe that nature has provided 
                us with everything we need for optimal health and wellness.
              </p>
              <p>
                Our journey began with a passion for traditional herbal medicine and a commitment to 
                modern quality standards. We carefully select each ingredient, ensuring it meets our 
                strict criteria for purity, potency, and sustainability.
              </p>
              <p>
                Today, we are proud to serve thousands of customers who trust us for their health and 
                wellness needs. Every product we offer is backed by our promise of quality, authenticity, 
                and your satisfaction.
              </p>
            </div>
          </div>
        </div>

        {/* Features Grid */}
        <div className="mb-6 sm:mb-6">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2 sm:mb-6 text-center">
            Why Choose Us
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 p-6 sm:p-8 border border-gray-100 hover:border-green-300 transform hover:-translate-y-1"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center mb-4 sm:mb-6 mx-auto">
                  <feature.icon className="text-2xl text-white" />
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4 text-center">
                  {feature.title}
                </h3>
                <p className="text-gray-600 text-sm sm:text-base text-center leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Mission, Vision, Values */}
        <div className="mb-6 sm:mb-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
            {values.map((value, index) => (
              <div
                key={index}
                className="bg-gradient-to-br text-center from-green-50 to-emerald-50 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 p-6 sm:p-8 border border-green-100"
              >
                <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4 sm:mb-6 text-center">
                  {value.title}
                </h3>
                <p className="text-gray-700 text-sm sm:text-base leading-relaxed text-center">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Stats Section */}
        <div className="bg-gradient-to-r from-green-600 to-emerald-700 rounded-2xl shadow-xl p-8 sm:p-12 text-white mb-6 sm:mb-6">
          <div className="grid  gap-6 sm:gap-8">
            <div className="text-center">
              <div className="text-4xl sm:text-5xl font-bold mb-2">100+</div>
              <div className="text-green-100 text-sm sm:text-base">Happy Customers</div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-white rounded-2xl shadow-lg p-8 text-center border border-gray-100">
          <Globe className="w-12 h-12 sm:w-16 sm:h-16 text-green-600 mb-6 mx-auto" strokeWidth={1.5} />
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4 sm:mb-6">
            Join Our Community
          </h2>
          <p className="text-lg sm:text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            Experience the difference that natural, quality herbal products can make in your life. 
            Start your wellness journey with us today.
          </p>
          <div className="flex flex-row gap-4 justify-center items-center">
            <a
              href="/dashboard"
              className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white px-8 py-4 rounded-xl font-semibold text-lg transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              Shop Now
            </a>
            <a
              href="/contact"
              className="bg-white border-2 border-green-500 text-green-500 hover:bg-green-50 px-8 py-4 rounded-xl font-semibold text-lg transition-all duration-200 shadow-md hover:shadow-lg"
            >
              Contact Us
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutUs;

